import axios from '../lib/axios';

export const getContacts = async (params = {}) => {
    const { data } = await axios.get('/api/contacts', { params });
    return data; // Returns { data: [...], meta: [...], links: [...] }
};

export const getContact = async (id) => {
    const { data } = await axios.get(`/api/contacts/${id}`);
    return data;
};

export const createContact = async (contactData) => {
    const { data } = await axios.post('/api/contacts', contactData);
    return data;
};

export const updateContact = async ({ id, ...contactData }) => {
    const { data } = await axios.put(`/api/contacts/${id}`, contactData);
    return data;
};

export const deleteContact = async (id) => {
    const { data } = await axios.delete(`/api/contacts/${id}`);
    return data;
};

export const getConversations = async (params = {}) => {
    const { data } = await axios.get('/api/conversations', { params });
    return data;
};

export const getConversation = async (id) => {
    const { data } = await axios.get(`/api/conversations/${id}`);
    return data;
};

export const getConversationMessages = async (id) => {
    const { data } = await axios.get(`/api/conversations/${id}/messages`);
    return data;
};

export const sendMessage = async (messageData) => {
    const { data } = await axios.post('/api/conversations/send-message', messageData);
    return data;
};

export const markConversationAsRead = async (id) => {
    const { data } = await axios.post(`/api/conversations/${id}/mark-as-read`);
    return data;
};

export const sendTypingPresence = async (id) => {
    try {
        const { data } = await axios.post(`/api/conversations/${id}/typing`);
        return data;
    } catch (_) {
        // Silencioso — la presencia es best-effort, no bloquea nada
    }
};

/**
 * Envía una reacción de emoji a un mensaje del cliente en WhatsApp.
 * Fire-and-forget — nunca bloquea la UI.
 * @param {number} conversationId
 * @param {number} messageId - ID del mensaje en la base de datos
 * @param {string} emoji - Emoji a reaccionar (ej: "👍")
 */
export const sendReaction = async (conversationId, messageId, emoji) => {
    try {
        const { data } = await axios.post(`/api/conversations/${conversationId}/react`, {
            message_id: messageId,
            emoji,
        });
        return data;
    } catch (_) {
        // Silencioso — las reacciones son opcionales
    }
};

/**
 * Eliminar un mensaje de la conversación.
 * @param {number} conversationId
 * @param {number} messageId
 * @param {boolean} forEveryone - Si es true, solicita a Evolution API la revocación para todos los participantes.
 */
export const deleteMessage = async (conversationId, messageId, forEveryone = false) => {
    const { data } = await axios.delete(`/api/conversations/${conversationId}/messages/${messageId}`, {
        params: { for_everyone: forEveryone ? 1 : 0 },
    });
    return data;
};

/**
 * Enviar mensaje multimedia o nota de voz a la conversación.
 */
export const sendMediaMessage = async (conversationId, file, type, caption = '', duration = 0, parentMessageId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('caption', caption);
    if (duration > 0) formData.append('duration', duration);
    if (parentMessageId) formData.append('parent_message_id', parentMessageId);

    const { data } = await axios.post(`/api/conversations/${conversationId}/send-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
};

/**
 * Biblioteca de stickers del tenant, más usados primero.
 */
export const fetchStickers = async () => {
    const { data } = await axios.get('/api/stickers');
    return data;
};

/**
 * Guardar un sticker en la biblioteca: subiendo el archivo, o tomándolo de un
 * mensaje que ya está en el chat.
 */
export const saveSticker = async ({ file, messageId, label }) => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (messageId) formData.append('message_id', messageId);
    if (label) formData.append('label', label);

    const { data } = await axios.post('/api/stickers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
};

export const deleteSticker = async (stickerId) => {
    const { data } = await axios.delete(`/api/stickers/${stickerId}`);
    return data;
};

/**
 * Enviar un sticker de la biblioteca. Puede volver 429 con el motivo cuando el
 * freno anti-bloqueo lo detiene: ese texto se le muestra tal cual al vendedor.
 */
export const sendStickerMessage = async (conversationId, stickerId, parentMessageId = null) => {
    const { data } = await axios.post(`/api/conversations/${conversationId}/send-sticker`, {
        sticker_id: stickerId,
        ...(parentMessageId ? { parent_message_id: parentMessageId } : {}),
    });
    return data;
};

/**
 * Buscar contactos por nombre, teléfono o email (para el modal de unificación).
 */
export const searchContacts = async (query) => {
    const { data } = await axios.get('/api/contacts', { params: { search: query, per_page: 10 } });
    return data.data;
};

/**
 * Unificar dos contactos. El master absorbe todos los datos del source.
 * El source queda archivado (no borrado).
 */
export const mergeContacts = async (masterId, sourceId, chosenName) => {
    const { data } = await axios.post(`/api/contacts/${masterId}/merge`, {
        source_contact_id: sourceId,
        chosen_name: chosenName,
    });
    return data;
};

/**
 * Obtener la cantidad de contactos archivados (unificados) del tenant.
 */
export const getMergedContactsCount = async () => {
    const { data } = await axios.get('/api/contacts/merged-count');
    return data;
};

/**
 * Purgar permanentemente todos los contactos archivados (requiere rol admin/manager).
 */
export const purgeMergedContacts = async () => {
    const { data } = await axios.delete('/api/contacts/purge-merged');
    return data;
};

/**
 * Simulator / AI Sandbox methods
 */
export const createAiContact = async (contactData) => {
    const { data } = await axios.post('/api/ai-emulator/create-contact', contactData);
    return data;
};

export const simulateAiMessage = async (messageData) => {
    const isMultipart = messageData instanceof FormData;
    const config = isMultipart ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    const { data } = await axios.post('/api/ai-emulator/simulate', messageData, config);
    return data;
};

export const clearAiHistory = async (contactId) => {
    const { data } = await axios.post('/api/ai-emulator/clear-history', { contact_id: contactId });
    return data;
};

/**
 * Borra un contacto de prueba del sandbox con sus mensajes y evaluaciones.
 * El backend se niega (422) si el contacto tambien tiene conversaciones reales.
 */
export const deleteEmulatedContact = async (contactId) => {
    const { data } = await axios.delete(`/api/ai-emulator/contacts/${contactId}`);
    return data;
};

export const getEmulatedContacts = async (tenantId) => {
    const { data } = await axios.get('/api/ai-emulator/contacts', { params: { tenant_id: tenantId } });
    return data;
};

export const refreshContactAvatar = async (id) => {
    const { data } = await axios.post(`/api/contacts/${id}/refresh-avatar`);
    return data;
};

/**
 * Trigger on-demand video download for a deferred video message.
 * @param {number} messageId - The CRM message ID
 * @returns {Promise<Object>} Updated message resource with media_url set
 */
export const downloadMessageMedia = async (messageId) => {
    const { data } = await axios.post(`/api/messages/${messageId}/download`);
    return data;
};

export const getWhatsappCatalog = async (refresh = false) => {
    const { data } = await axios.get('/api/whatsapp/catalog', { params: { refresh: refresh ? 1 : 0 } });
    return data;
};
